import { Component, OnInit, inject, computed } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { forkJoin, map, Observable } from 'rxjs'
import { catchError } from 'rxjs/operators'

import {
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  HuntTemplate,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DIALOG_DATA } from '@hyperkit'

import { SessionService } from '@hunt/services'

import { isHuntPackageHuntCompatible } from '../../../../helpers/utils'
import { CompanyService } from '../../../shared/services/company.service'
import { ContentService } from '../../../shared/services/content.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import { HuntTemplateService } from '../../../shared/services/hunt-template.service'

@Component({
  standalone: false,
  selector: 'edit-template-dialog',
  templateUrl: './edit-template-dialog.component.html',
  styleUrls: ['./edit-template-dialog.component.scss'],
})
export class EditTemplateDialogComponent implements OnInit {
  dialogRef = inject(DialogRef)
  private formBuilder = inject(FormBuilder)
  private huntTemplateService = inject(HuntTemplateService)
  private companyService = inject(CompanyService)
  private environmentService = inject(EnvironmentService)
  huntTemplateId = inject<string>(DIALOG_DATA, { optional: true })
  private contentService = inject(ContentService)

  public formGroup: FormGroup
  public priorities: Array<string> = []
  public repeatOptions: Array<string> = ['Daily', 'Weekly', 'Monthly']
  public everyOptions: Array<number> = [...Array(99).keys()]
  public assignees$: Observable<Array<Profile>>
  public environments: Environment[]
  public generatedHuntPackages: ExpandedHuntPackageRecord[]
  public error: string
  public huntTemplate: HuntTemplate
  public isLoading = true

  public ngOnInit(): void {
    this.huntTemplateService
      .getHuntTemplateById(this.huntTemplateId)
      .pipe(
        catchError((err) => {
          this.error = err
          this.isLoading = false
          return []
        }),
      )
      .subscribe({
        next: (huntTemplate: HuntTemplate) => {
          this.huntTemplate = huntTemplate
          this.initializeForm()
          this.isLoading = false
        },
        error: (err) => {
          this.error = err
          this.isLoading = false
        },
      })

    this.environmentService
      .getAllEnvironments()
      .pipe(
        catchError((err) => {
          this.error = err
          return []
        }),
      )
      .subscribe((environments) => {
        this.environments = environments
      })

    this.priorities = Object.values(Enums.PriorityEnum)
  }

  private initializeForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [this.huntTemplate.name, Validators.required],
      environments: new FormControl(this.huntTemplate.environments || []),
      priority: [this.huntTemplate.priority, Validators.required],
      defaultAssignee: [this.huntTemplate.defaultAssignee],
      hypothesis: [this.huntTemplate.hypothesis, Validators.required],
      description: [this.huntTemplate.description, Validators.required],
    })

    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            fullName: `${user.firstName} ${user.lastName}`,
          }
        })
      }),
      catchError((err) => {
        this.error = err
        return []
      }),
    )
  }

  public onValueChange(): void {
    const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
      this.huntTemplate.huntPackages
        .filter((huntPackage) => {
          return isHuntPackageHuntCompatible(huntPackage)
        })
        .map((huntPackage) => {
          return this.contentService
            .getHuntPackageContent(huntPackage.UUID, {
              environments: this.formGroup.value.environments,
            })
            .pipe(
              map((generatedContent) => {
                const expanded = huntPackage as ExpandedHuntPackageRecord
                expanded.analysis = expanded.originalAnalysis = generatedContent
                return expanded
              }),
              catchError((err) => {
                this.error = err
                return []
              }),
            )
        })

    forkJoin(generatedHuntPackages)
      .pipe(
        catchError((err) => {
          this.error = err
          return []
        }),
      )
      .subscribe((generatedHuntPacks) => {
        this.generatedHuntPackages = generatedHuntPacks
      })
  }

  public onSubmit(): void {
    const template: HuntTemplate = this.formGroup.value
    template.environments = this.environments.filter((env) =>
      this.formGroup.value.environments.includes(env.id),
    )
    template.huntPackages = this.generatedHuntPackages

    this.huntTemplateService
      .updateHuntTemplate(this.huntTemplate.id, template)
      .pipe(
        catchError((err) => {
          this.error = err
          return []
        }),
      )
      .subscribe((hunt: HuntTemplate) => {
        this.dialogRef.close(hunt)
      })
  }

  public onClose(): void {
    this.dialogRef.close()
  }
}
